import {
  WSButton,
  WSElement,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { WorkLogStatus } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import { Tabs } from "../../../components/Tabs";
import { useQueryWorkLogsSummary } from "../../../query/workLog/queries/useQueryWorkLogsSummary";
import { WorklogsPayerDashboard } from "../components/WorklogsPayerDashboard";
import { FilterableTableWorkLogsPayerView } from "../components/TableWorkLogsPayerView/FilterableTableWorkLogsPayerView";
import { WorkLogPayerTabFilterStatus } from "../components/TableWorkLogsPayerView/filters/types";
import {
  routeHelpersWorkLogsPayer,
  routeHelpersWorkLogsPayerApproved,
  routeHelpersWorkLogsPayerDashboard,
  routeHelpersWorkLogsPayerRejected,
  routeHelpersWorkLogsPayerInProgress,
  routeHelpersWorkLogsPayerToApprove
} from "../paths";
import { useSession } from "@hooks/useSession";

export const RoutePayerLogs = () => {
  const history = useHistory();
  const { accountId } = useSession();

  const queryHasAnyWorkLogs = useQueryWorkLogsSummary({
    filter: { payerAccountId: { eq: accountId } }
  });
  const hasAnyWorkLogs = (queryHasAnyWorkLogs.data?.listSize ?? 0) > 0;

  // Summary queries for tab counts
  const queryToApprove = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.IN_REVIEW] }
    }
  });

  const queryApproved = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.APPROVED] }
    }
  });

  const queryInProgress = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.IN_PROGRESS] }
    }
  });

  const queryRejected = useQueryWorkLogsSummary({
    filter: {
      payerAccountId: { eq: accountId },
      status: { anyOf: [WorkLogStatus.REJECTED] }
    }
  });

  // Build tabs based on work logs existence
  const tabs = useMemo(() => {
    const allTabs = [
      {
        label: "Dashboard",
        path: routeHelpersWorkLogsPayerDashboard.path
      },
      {
        label: "To approve",
        path: routeHelpersWorkLogsPayerToApprove.path,
        badge: queryToApprove.data?.listSize
      },
      {
        label: "Approved",
        path: routeHelpersWorkLogsPayerApproved.path,
        badge: queryApproved.data?.listSize
      },
      {
        label: "Rejected",
        path: routeHelpersWorkLogsPayerRejected.path,
        badge: queryRejected.data?.listSize
      },
      {
        label: "In-progress",
        path: routeHelpersWorkLogsPayerInProgress.path,
        badge: queryInProgress.data?.listSize
      }
    ];

    // Only show Dashboard tab when no work logs exist
    if (!hasAnyWorkLogs) {
      return [allTabs[0]];
    }

    return allTabs;
  }, [
    hasAnyWorkLogs,
    queryToApprove.data?.listSize,
    queryApproved.data?.listSize,
    queryInProgress.data?.listSize,
    queryRejected.data?.listSize
  ]);

  return (
    <WSPage>
      <WSElement mb="2XL">
        <WSPageToolbar title="Work logs" />
        <WSText.ParagraphSm color="gray500">
          Review contractor work logs before they become payables. Once
          approved, payables are automatically generated on your{" "}
          <WSButton.Link
            onClick={() => history.push("/member/invoices/payables")}
          >
            Payables
          </WSButton.Link>{" "}
          page.
        </WSText.ParagraphSm>
      </WSElement>

      <Tabs mb="L" tabs={tabs} />
      <Switch>
        <Route
          path={routeHelpersWorkLogsPayerDashboard.path}
          component={WorklogsPayerDashboard}
        />
        <Route path={routeHelpersWorkLogsPayerToApprove.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.ToApprove}
            enableBulkActions
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayerApproved.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.Approved}
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayerRejected.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.Rejected}
          />
        </Route>
        <Route path={routeHelpersWorkLogsPayerInProgress.path}>
          <FilterableTableWorkLogsPayerView
            status={WorkLogPayerTabFilterStatus.InProgress}
          />
        </Route>
        <Redirect
          from={routeHelpersWorkLogsPayer.path}
          to={routeHelpersWorkLogsPayerDashboard.path}
        />
      </Switch>
    </WSPage>
  );
};
